import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Menu } from 'lucide-react'

import { AuthGuard } from '@/components/guards/AuthGuard'
import { Sidebar } from '@/components/layout/Sidebar'
import { useMe } from '@/queries/auth'

const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'))
const BuyerDashboard = lazy(() => import('@/pages/buyer/Dashboard'))
const SupplierDashboard = lazy(() => import('@/pages/supplier/Dashboard'))

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false)
    window.setTimeout(() => menuButtonRef.current?.focus(), 0)
  }, [])

  useEffect(() => {
    if (!sidebarOpen) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSidebar()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closeSidebar, sidebarOpen])

  return (
    <AuthGuard>
      <div className="flex h-dvh overflow-hidden bg-background">
        <Sidebar mobileOpen={sidebarOpen} onMobileClose={closeSidebar} />
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Menü dışına çık"
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            onClick={closeSidebar}
          />
        )}
        <div aria-hidden={sidebarOpen || undefined} className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 flex-none items-center border-b border-gray-200 bg-white px-4 md:hidden">
            <button
              ref={menuButtonRef}
              type="button"
              aria-label="Menüyü aç"
              aria-controls="app-sidebar"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(true)}
              className="rounded-md p-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="ml-3 font-semibold text-brand-600">TedarikSen</span>
          </header>
          <main className="min-w-0 flex-1 overflow-auto">
            <Suspense
              fallback={
                <div role="status" aria-live="polite" className="p-4 text-sm text-gray-500 sm:p-8">
                  Yükleniyor...
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </AuthGuard>
  )
}

export function RoleDashboard() {
  const { data, isLoading, error, refetch } = useMe()
  if (isLoading)
    return (
      <div role="status" className="p-4 sm:p-8">
        Yükleniyor...
      </div>
    )
  if (error) {
    return (
      <div role="alert" className="p-4 text-sm text-red-700 sm:p-8">
        <p>{error instanceof Error ? error.message : 'Oturum bilgisi yüklenemedi.'}</p>
        <button type="button" onClick={() => void refetch()} className="mt-3 font-medium underline">
          Tekrar dene
        </button>
      </div>
    )
  }
  switch (data?.user?.role) {
    case 'ADMIN':
      return <AdminDashboard />
    case 'SUPPLIER':
      return <SupplierDashboard />
    case 'BUYER':
    default:
      return <BuyerDashboard />
  }
}
